"use client";

// Universal tracking-event hook for ViewContent / InitiateCheckout /
// PageView / Purchase. Fires the browser pixel (consent-gated by
// CookieConsent.js + `__loadTrackingPixels`) AND posts the same event
// to `/api/track/event` for server-side dispatch. Same `eventId` on
// both sides → Meta + TikTok merge them into one counted event.
//
// Casper 2026-05-13 directive — 100% retargeting coverage. Server-side
// dispatch currently still forwards a `consent` flag for visibility;
// PR-4 switches the BE to a `bypassConsent: true` mode that ignores
// it (audit-trail preserved in `tracking_events.meta_data`).
//
// Pure payload helpers live in `track-event-payload.mjs` so they can
// be unit-tested without React or a DOM (see `track-event-payload.test.mjs`).

import { useEffect, useRef } from "react";
import {
  buildTrackEventBody,
  generateEventId,
  safeTrackingPathname,
  readMarketingConsent,
} from "@/lib/track-event-payload.mjs";
import { ensureClientIdInBrowser } from "@/lib/client-id-cookie.mjs";
import { readEmailHash } from "@/lib/email-hash-storage.mjs";

const ENDPOINT = "/api/track/event";

/**
 * Fire-and-forget POST to the BE ingest endpoint. Returns the promise
 * so callers can `.catch()` to swallow network errors.
 */
function postToBackend(params, marketingConsent) {
  if (typeof fetch === "undefined") return Promise.resolve();
  try {
    const payload = buildTrackEventBody({
      eventName: params.eventName,
      eventId: params.eventId,
      eventSourceUrl:
        params.eventSourceUrl ||
        (typeof window !== "undefined" ? window.location.href : undefined),
      amount: params.amount,
      currency: params.currency,
      productId: params.productId,
      productName: params.productName,
      email: params.email,
      phone: params.phone,
      cookieString:
        typeof document !== "undefined" ? document.cookie : undefined,
      consent: marketingConsent,
      // P1a 2026-05-15 — stable per-browser id read or minted from
      // the `favotrip_cid` cookie. Drives Meta + TikTok external_id
      // for cross-event identity (PageView → Purchase trail).
      clientId: ensureClientIdInBrowser(),
      // P1b 2026-05-15 — pre-hashed email persisted on form-submit
      // by `email-hash-storage.mjs`. Browser hashes BEFORE storing
      // so plaintext never crosses the wire. BE plugs it straight
      // into `user_data.em` for Meta + `user.email` for TikTok,
      // boosting Event Match Quality on every event after the first
      // form-capture (cross-session identity).
      emailHash:
        typeof window !== "undefined"
          ? readEmailHash(window.localStorage)
          : undefined,
    });
    return fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true, // survives page-unload (critical for unload-time events)
    });
  } catch (err) {
    if (typeof console !== "undefined") {
       
      console.warn("[useTrackEvent] backend post failed:", err);
    }
    return Promise.resolve();
  }
}

/**
 * Fire the browser pixel for an event. No-op when pixels aren't loaded
 * (e.g. user denied marketing consent → `__loadTrackingPixels` never
 * ran → `window.fbq` / `window.ttq` undefined).
 */
function firePixel(params) {
  if (typeof window === "undefined") return;

  if (typeof window.fbq === "function") {
    try {
      const fbCustomData = {};
      if (params.productId)
        fbCustomData.content_ids = [String(params.productId)];
      if (params.productName) fbCustomData.content_name = params.productName;
      if (params.productId || params.productName)
        fbCustomData.content_type = "product";
      if (params.amount !== undefined) fbCustomData.value = params.amount;
      if (params.currency) fbCustomData.currency = params.currency;

      window.fbq(
        "track",
        params.eventName,
        Object.keys(fbCustomData).length ? fbCustomData : undefined,
        { eventID: params.eventId },
      );
    } catch (err) {
      if (typeof console !== "undefined") {
         
        console.warn("[useTrackEvent] fbq failed:", err);
      }
    }
  }

  if (typeof window.ttq === "object" && window.ttq) {
    try {
      if (params.eventName === "PageView") {
        if (typeof window.ttq.page === "function") {
          window.ttq.page({ event_id: params.eventId });
        }
      } else if (typeof window.ttq.track === "function") {
        const ttProps = { event_id: params.eventId };
        if (params.productId || params.productName) {
          ttProps.contents = [
            {
              content_id: params.productId
                ? String(params.productId)
                : "voucher",
              content_type: "product",
              ...(params.productName && { content_name: params.productName }),
              ...(params.amount !== undefined && { price: params.amount }),
              quantity: 1,
            },
          ];
        }
        if (params.amount !== undefined) {
          ttProps.value = params.amount;
          ttProps.currency = params.currency || "EUR";
        }
        const ttEventName =
          params.eventName === "Purchase"
            ? "CompletePayment"
            : params.eventName;
        window.ttq.track(ttEventName, ttProps);
      }
    } catch (err) {
      if (typeof console !== "undefined") {
         
        console.warn("[useTrackEvent] ttq failed:", err);
      }
    }
  }

  // OpenAI (ChatGPT Ads) pixel — 2026-09-03. `window.oaiq` only exists
  // once layout.js's consent-gated __loadTrackingPixels has injected the
  // SDK, and trackEvent() above already skips firePixel() entirely on a
  // sensitive path -- so this branch inherits both gates for free, exactly
  // like fbq/ttq. Event names map to OAIQ's standard vocabulary
  // (developers.openai.com/ads/measurement-pixel):
  //   PageView -> page_viewed, InitiateCheckout -> checkout_started,
  //   Purchase -> order_created.
  // ViewContent has no OAIQ standard equivalent and is deliberately NOT
  // sent (no invented custom event).
  // HONEST SCOPE NOTE (Codex round 1 on PR #1307): as of 2026-09-04 no
  // production caller passes eventName: "Purchase" through trackEvent()
  // (callers: TrackingPageView, TrackingViewContent,
  // TrackingInitiateCheckout only). The REAL voucher conversion fires
  // directly in components/voucher/VoucherThankYou.js, inside its
  // hasMarketingConsent() block, next to tap/ttq/fbq -- that is where
  // order_created is actually reported. The Purchase mapping here stays
  // for correctness if a future flow routes purchases through
  // trackEvent(), but it is NOT what measures today's sales.
  // `amount` is forwarded in major units (EUR 129.00 -> 129), identical to
  // the fbq `value` / ttq `value` above. The OAIQ docs' own example
  // (`amount: 2599`) is ambiguous about minor units -- verify ONE real
  // purchase in Ads Manager > Conversies > Gebeurtenisstroom before trusting
  // any reported ROAS/value figure.
  if (typeof window.oaiq === "function") {
    try {
      const OAIQ_EVENT_NAME = {
        PageView: "page_viewed",
        InitiateCheckout: "checkout_started",
        Purchase: "order_created",
      };
      const oaiEventName = OAIQ_EVENT_NAME[params.eventName];
      if (oaiEventName) {
        const oaiProps = {};
        if (params.amount !== undefined) {
          oaiProps.amount = params.amount;
          oaiProps.currency = params.currency || "EUR";
        }
        if (Object.keys(oaiProps).length) {
          window.oaiq("measure", oaiEventName, oaiProps);
        } else {
          window.oaiq("measure", oaiEventName);
        }
      }
    } catch (err) {
      if (typeof console !== "undefined") {
        console.warn("[useTrackEvent] oaiq failed:", err);
      }
    }
  }
}

/**
 * Synchronous trigger — exported so deeper components can fire on a
 * button click (`onClick={() => trackEvent({...})}`) instead of relying
 * on the hook's mount-time effect.
 *
 * Codex review (site-frontend PR #1252, round 4, HIGH, addressed): a
 * caller-supplied (or caller-OMITTED -- `postToBackend` itself falls back
 * to the raw `window.location.href`) `eventSourceUrl` is not a reliable
 * enough guarantee that a sensitive path's query string never leaks --
 * every future call site would have to remember to redact it itself.
 * This function is the ONE place every `trackEvent` call passes through,
 * so the sensitive-path check lives HERE, centrally, applied
 * unconditionally regardless of what the caller passed:
 *   - the CLIENT pixel (`firePixel` -> `fbq`/`ttq`, which read
 *     `window.location` internally with no redaction hook at all) is
 *     skipped ENTIRELY on a sensitive path;
 *   - the SERVER CAPI dispatch (`postToBackend`) still fires (so the
 *     conversion signal is not silently lost for a page that legitimately
 *     wants one), but with `eventSourceUrl` force-overridden to the
 *     query-string-free path, never trusting the caller's value.
 *
 * Codex review (site-frontend PR #1450, addressed): this used to build
 * the "safe" `eventSourceUrl` as `origin + window.location.pathname` --
 * the RAW pathname. That only strips the query string/fragment, which is
 * all every route needed until `/zakelijk/beheer/[token]` -- there the
 * secret IS a path segment, so the raw pathname still carried it through
 * to the backend collector. Uses `safeTrackingPathname` (the matched
 * PREFIX for a path-segment-secret route like this one, e.g.
 * `/zakelijk/beheer`; the unchanged pathname for every other sensitive
 * route, exactly like before) instead of the raw pathname directly.
 */
export function trackEvent(params) {
  const eventId = params.eventId || generateEventId(params.eventName);
  const safePathname =
    typeof window !== "undefined"
      ? safeTrackingPathname(window.location.pathname)
      : null;
  const sensitivePath = safePathname !== null;
  const merged = {
    ...params,
    eventId,
    eventSourceUrl: sensitivePath
      ? `${window.location.origin}${safePathname}`
      : params.eventSourceUrl,
  };
  const consent =
    typeof window !== "undefined"
      ? readMarketingConsent(window.localStorage)
      : false;
  if (!sensitivePath) {
    firePixel(merged);
  }
  postToBackend(merged, consent).catch(() => {
    /* swallow; logged inside */
  });
  return eventId;
}

/**
 * React hook variant — fires `trackEvent(params)` once on mount.
 * Guarded against React strict-mode double-effect via a ref.
 */
export function useTrackEvent(params) {
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    if (!params || !params.eventName) return;
    firedRef.current = true;
    trackEvent(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
